/**
 * Helper Functions for Crop Data Processing
 */

/**
 * Parse a time string into minutes since midnight.
 * Supports both 24-hour ("15:45") and 12-hour ("6:30 PM") formats.
 * @param {string} time - Time string
 * @returns {number} Minutes since midnight
 */
const parseTimeToMinutes = (time) => {
  const match = String(time).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) return 0;

  let hour = Number(match[1]);
  const min = Number(match[2]);
  const meridiem = match[3] ? match[3].toUpperCase() : null;

  if (meridiem === 'PM' && hour !== 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;

  return hour * 60 + min;
};

/**
 * Calculate duration between two times
 * @param {string} onTime - Start time (24-hour "22:30" or 12-hour "10:30 PM")
 * @param {string} offTime - End time in the same accepted formats
 * @returns {string} Duration in "Xh Ym" format
 */
export const calculateDuration = (onTime, offTime) => {
  let diff = parseTimeToMinutes(offTime) - parseTimeToMinutes(onTime);
  if (diff < 0) diff += 24 * 60; // wrap past midnight

  const durationHours = Math.floor(diff / 60);
  const durationMins = diff % 60;

  return `${durationHours}h ${durationMins}m`;
};

/**
 * Get localized crop data
 * @param {Object} crop - Crop object
 * @param {string} language - Language code ('en', 'hi', 'gu')
 * @returns {Object} Localized name and pest
 */
export const getLocalizedCrop = (crop, language) => {
  if (language === 'hi') {
    return {
      name: crop.hi?.name || crop.name,
      pest: crop.hi?.pest || crop.pest,
    };
  } else if (language === 'gu') {
    return {
      name: crop.gu?.name || crop.name,
      pest: crop.gu?.pest || crop.pest,
    };
  }
  return {
    name: crop.name,
    pest: crop.pest,
  };
};

/**
 * Get all unique categories from crops
 * @param {Array} crops - Array of crop objects
 * @returns {Array} Sorted array of unique categories
 */
export const getCategories = (crops) => {
  return [...new Set(crops.map(crop => crop.category))].sort();
};

/**
 * Filter crops based on search term and category
 * @param {Array} crops - Array of crop objects
 * @param {string} searchTerm - Search query
 * @param {string} selectedCategory - Category filter
 * @returns {Array} Filtered crops
 */
export const filterCrops = (crops, searchTerm, selectedCategory) => {
  return crops.filter(crop => {
    const matchesSearch = 
      crop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crop.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crop.pest.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || crop.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
};

/**
 * Get light spectrum type
 * @param {string} light - Light spectrum string
 * @returns {string} Type of light ('Heat' or 'UV')
 */
export const getLightSpectrumType = (light) => {
  return light.includes('Heat') ? 'Heat' : 'UV';
};
